import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useThemeMode } from "../../ThemeContext";
import { dataAPI } from "../../helpers/api_service";
import { APP_URLS } from "../../helpers/url_helper";

const ROWS_PER_PAGE = 10;

const pickNameFromObject = (value) => {
  if (!value || typeof value !== "object") return "";
  return (
    value.full_name ||
    value.name ||
    value.username ||
    value.email ||
    ""
  );
};

const getSubmittedByName = (row) => {
  const submittedBy = row?.submitted_by;
  if (typeof submittedBy === "string" && submittedBy.trim()) {
    return submittedBy.trim();
  }
  const fromSubmittedBy = pickNameFromObject(submittedBy);
  if (fromSubmittedBy) return fromSubmittedBy;

  return (
    row?.submitted_by_full_name ||
    row?.submitted_by_name ||
    row?.submitted_by_user_full_name ||
    row?.submitted_user_full_name ||
    row?.submitted_by_user?.full_name ||
    row?.submitted_by_user?.name ||
    row?.submitted_by_details?.full_name ||
    row?.submitted_by_details?.name ||
    ""
  );
};

const getSubmittedByDisplay = (row) => {
  const userName = getSubmittedByName(row);
  return userName || "-";
};

const getSubmittedByRole = (row) => {
  const submittedBy = row?.submitted_by;
  if (submittedBy && typeof submittedBy === "object") {
    return (
      submittedBy.role_title ||
      submittedBy.role_name ||
      submittedBy.role ||
      "-"
    );
  }

  return (
    row?.submitted_by_role_title ||
    row?.submitted_role_title ||
    row?.submitted_by_role ||
    row?.submitted_by_user?.role_title ||
    row?.submitted_by_user?.role_name ||
    row?.submitted_by_details?.role_title ||
    row?.submitted_by_details?.role_name ||
    "-"
  );
};

function CompanyFormRecords() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const form = location.state?.form || null;
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchSubmittedBy, setSearchSubmittedBy] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const loadRecords = async () => {
      const formId = form?.id;
      if (!formId) {
        setIsLoading(false);
        setErrorMessage("Form id is missing. Please open records from list page.");
        return;
      }

      try {
        setIsLoading(true);
        setErrorMessage("");
        const response = await dataAPI.getDigitalFormClientFormSubmissions(formId);
        const data = response?.data || {};
        const apiRows = Array.isArray(data.rows) ? data.rows : [];
        const normalizedRows = apiRows.map((row) => {
          if (Array.isArray(row?.answers)) return row;
          if (typeof row?.answers === "string") {
            try {
              const parsedAnswers = JSON.parse(row.answers);
              return {
                ...row,
                answers: Array.isArray(parsedAnswers) ? parsedAnswers : [],
              };
            } catch (error) {
              return {
                ...row,
                answers: [],
              };
            }
          }
          return {
            ...row,
            answers: [],
          };
        });
        setRows(normalizedRows);
      } catch (error) {
        setRows([]);
        setErrorMessage(error?.message || "Failed to load form records.");
      } finally {
        setIsLoading(false);
      }
    };

    loadRecords();
  }, [form]);

  useEffect(() => {
    setPage(0);
  }, [searchSubmittedBy, rows.length]);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    const time = parsed.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const filteredRows = useMemo(() => {
    const query = searchSubmittedBy.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      getSubmittedByDisplay(row).toLowerCase().includes(query),
    );
  }, [rows, searchSubmittedBy]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));

  const paginatedRows = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return filteredRows.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRows, page]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const showPagination = filteredRows.length > ROWS_PER_PAGE;
  const rangeStart = filteredRows.length === 0 ? 0 : page * ROWS_PER_PAGE + 1;
  const rangeEnd = Math.min((page + 1) * ROWS_PER_PAGE, filteredRows.length);

  return (
    <div style={{ padding: 20 }}>
      <div style={wrapperStyle(mode)}>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to Company Forms
        </button>

        <h2 style={titleStyle(mode)}>{form?.title || "Company Form Records"}</h2>
        <p style={descriptionStyle(mode)}>{form?.description || "No description"}</p>

        {!isLoading && errorMessage && (
          <div style={errorStyle(mode)}>
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && rows.length === 0 && (
          <div style={infoStyle(mode)}>
            No records found.
          </div>
        )}

        {!isLoading && !errorMessage && rows.length > 0 && (
          <>
            <div style={{ marginBottom: 14 }}>
              <input
                type="text"
                value={searchSubmittedBy}
                onChange={(event) => setSearchSubmittedBy(event.target.value)}
                placeholder="Search by submitted by..."
                style={searchInputStyle(mode)}
              />
            </div>

            {filteredRows.length === 0 ? (
              <div style={infoStyle(mode)}>No records match your search.</div>
            ) : (
              <>
                <div style={tableWrapperStyle(mode)}>
            <table style={tableStyle()}>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={headCellStyle(mode)}>Sr no.</th>
                  <th style={headCellStyle(mode)}>Submitted by</th>
                  <th style={headCellStyle(mode)}>User role</th>
                  <th style={headCellStyle(mode)}>Submitted at</th>
                  <th style={{ ...headCellStyle(mode), textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, index) => (
                  <tr key={row.id}>
                    <td style={bodyCellStyle(mode)}>{page * ROWS_PER_PAGE + index + 1}</td>
                    <td style={submitterCellStyle(mode)}>{getSubmittedByDisplay(row)}</td>
                    <td style={bodyCellStyle(mode)}>{getSubmittedByRole(row)}</td>
                    <td style={bodyCellStyle(mode)}>{formatDateTime(row?.submitted_at)}</td>
                    <td style={{ ...bodyCellStyle(mode), textAlign: "center" }}>
                      <button
                        type="button"
                        style={viewBtnStyle()}
                        onClick={() =>
                          navigate(APP_URLS.COMPANY_FORM_RECORD_VIEW, {
                            state: { form, submission: row },
                          })
                        }
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
                </div>

                {showPagination && (
                  <div style={paginationWrapStyle()}>
                    <span style={paginationInfoStyle(mode)}>
                      Showing {rangeStart}-{rangeEnd} of {filteredRows.length}
                    </span>
                    <div style={paginationControlsStyle()}>
                      <button
                        type="button"
                        onClick={() => setPage((current) => Math.max(0, current - 1))}
                        disabled={page === 0}
                        style={paginationBtnStyle(mode, page === 0)}
                      >
                        Previous
                      </button>
                      <span style={paginationInfoStyle(mode)}>
                        Page {page + 1} of {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setPage((current) => Math.min(totalPages - 1, current + 1))
                        }
                        disabled={page >= totalPages - 1}
                        style={paginationBtnStyle(mode, page >= totalPages - 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const wrapperStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 12,
});

const titleStyle = (mode) => ({
  margin: "0 0 6px",
  color: mode === "dark" ? "#fff" : "#0f172a",
});

const descriptionStyle = (mode) => ({
  margin: "0 0 14px",
  color: mode === "dark" ? "#98a2b3" : "#64748b",
  fontSize: 13,
});

const infoStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  fontSize: 14,
});

const errorStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#704040" : "#f1b2b2"}`,
  background: mode === "dark" ? "#2b1717" : "#fff3f3",
  color: mode === "dark" ? "#ffb0b0" : "#a53030",
  borderRadius: 8,
  padding: "12px 14px",
  fontSize: 14,
});

const tableWrapperStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  borderRadius: 10,
  overflowX: "auto",
});

const tableStyle = () => ({
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
});

const headCellStyle = (mode) => ({
  textAlign: "left",
  padding: "11px 12px",
  borderBottom: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  background: mode === "dark" ? "#171717" : "#f8fafc",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
  fontWeight: 600,
});

const bodyCellStyle = (mode) => ({
  padding: "11px 12px",
  color: mode === "dark" ? "#d0d5dd" : "#0f172a",
  borderBottom: `1px solid ${mode === "dark" ? "#2f2f2f" : "#f1f5f9"}`,
  fontSize: 13,
});

const submitterCellStyle = (mode) => ({
  ...bodyCellStyle(mode),
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.45,
});

const viewBtnStyle = () => ({
  border: "none",
  background: "#0ea5a4",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 14px",
  cursor: "pointer",
});

const searchInputStyle = (mode) => ({
  width: "100%",
  maxWidth: 360,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const paginationWrapStyle = () => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  marginTop: 14,
});

const paginationControlsStyle = () => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
});

const paginationInfoStyle = (mode) => ({
  fontSize: 12,
  color: mode === "dark" ? "#94a3b8" : "#64748b",
});

const paginationBtnStyle = (mode, isDisabled) => ({
  padding: "7px 12px",
  borderRadius: 8,
  border: `1px solid ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  background: "transparent",
  color: mode === "dark" ? "#e2e8f0" : "#334155",
  fontSize: 12,
  fontWeight: 600,
  cursor: isDisabled ? "not-allowed" : "pointer",
  opacity: isDisabled ? 0.55 : 1,
});

export default CompanyFormRecords;
